import { useId } from "react";
import type { BrandMark } from "./shape.ts";

/**
 * Baton's marks drawn inline in `currentColor` (DESIGN.md § Brand, 2026-09-28), so a mark takes the
 * ink of whatever it sits on, in both themes and under a Team's theme, and is never a black logo on
 * a dark ground. The caller sizes it by its block size alone (`--brand-logo-height`,
 * `--brand-mark-size`) with an automatic inline size, which the viewBox gives, so the mark keeps
 * its proportions.
 *
 * `label` is the accessible name, given where the mark is the only brand text on the surface; with
 * none the mark is hidden from assistive technology, for a surface whose text already names it.
 */
export interface BrandMarkProps {
  readonly label?: string;
  readonly className?: string;
}

/** How far the sheen's soft edge reaches either side of its brightest line, as a share of the canvas's side. */
const SHEEN_REACH = 0.18;

/** A coordinate at two decimals, so the layer's attributes carry no float noise. */
const at = (value: number) => Number(value.toFixed(2));

/**
 * The sheen layer, the one motion a mark has (DESIGN.md § Brand, 2026-09-29, by the owner's review of
 * the rail): a band in the mark's own ink, brightest on one line and fading to nothing either side of
 * it, standing square to the B's diagonal cut and clipped to the B, so it never draws outside the
 * mark. It sits on the canvas's centre; the caller's stylesheet runs it along the cut, from the lower
 * left to the upper right (`.b-brand-sheen`, a transform of the band's own box, 3 canvases square),
 * while it dims the ink under it (`.b-brand-ink`, opacity), which is what makes a band of the same
 * ink read as light. Square to the cut, the band always crosses the B's solid strokes on both sides
 * of the cut rather than hiding in it. The layer is mounted only while the sheen plays.
 */
function Sheen({ shape }: { readonly shape: BrandMark }) {
  const id = useId();
  const [x, y, side] = shape.viewBox.split(" ").map(Number) as [number, number, number, number];
  const centre = { x: x + side / 2, y: y + side / 2 };
  const reach = side * SHEEN_REACH;
  return (
    <>
      <defs>
        <clipPath id={`${id}clip`}>
          <path d={shape.d} fillRule={shape.fillRule} />
        </clipPath>
        <linearGradient id={`${id}band`} gradientUnits="userSpaceOnUse" x1={at(centre.x - reach)} y1={at(centre.y + reach)} x2={at(centre.x + reach)} y2={at(centre.y - reach)}>
          <stop offset="0" stopColor="currentColor" stopOpacity="0" />
          <stop offset="0.5" stopColor="currentColor" stopOpacity="1" />
          <stop offset="1" stopColor="currentColor" stopOpacity="0" />
        </linearGradient>
      </defs>
      <g clipPath={`url(#${id}clip)`}>
        <rect className="b-brand-sheen" x={at(x - side)} y={at(y - side)} width={at(side * 3)} height={at(side * 3)} fill={`url(#${id}band)`} />
      </g>
    </>
  );
}

export function Drawn({ shape, label, className, sheen = false }: BrandMarkProps & { readonly shape: BrandMark; readonly sheen?: boolean }) {
  const named = label === undefined ? { "aria-hidden": true } : { role: "img", "aria-label": label };
  return (
    <svg viewBox={shape.viewBox} className={className} fill="currentColor" focusable="false" {...named}>
      <path className={sheen ? "b-brand-ink" : undefined} d={shape.d} fillRule={shape.fillRule} stroke="currentColor" strokeWidth={shape.strokeWidth} strokeLinejoin={shape.strokeLinejoin} />
      {sheen && <Sheen shape={shape} />}
    </svg>
  );
}
